import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const Container = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
`;

const Separator = styled.span`
    font-size: 1.5rem;
    color: #999;
    user-select: none;
`;

const DigitInput = styled.input`
    width: 44px;
    height: 52px;
    text-align: center;
    font-size: 1.4rem;
    font-weight: 600;
    border: 2px solid ${({ $hasValue }) => $hasValue ? '#336384' : '#d0d5dd'};
    border-radius: 8px;
    outline: none;
    transition: border-color 0.15s ease;
    background: #fff;
    caret-color: #336384;

    &:focus {
        border-color: #336384;
        box-shadow: 0 0 0 3px rgba(51, 99, 132, 0.15);
    }

    &::selection {
        background: rgba(51, 99, 132, 0.2);
    }
`;

const OTPInput = ({ length = 6, value = '', onChange, autoFocus = false }) => {
    const [digits, setDigits] = useState(Array(length).fill(''));
    const inputsRef = useRef([]);

    // Sync external value into digits
    useEffect(() => {
        const chars = value.replace(/[^0-9]/g, '').split('').slice(0, length);
        const newDigits = Array(length).fill('');
        chars.forEach((c, i) => { newDigits[i] = c; });
        setDigits(newDigits);
    }, [value, length]);

    const emitChange = (newDigits) => {
        const code = newDigits.join('');
        onChange(code);
    };

    const focusInput = (index) => {
        if (index >= 0 && index < length) {
            inputsRef.current[index]?.focus();
            inputsRef.current[index]?.select();
        }
    };

    const handleChange = (e, index) => {
        const val = e.target.value;

        // Single digit typed
        if (/^\d$/.test(val)) {
            const newDigits = [...digits];
            newDigits[index] = val;
            setDigits(newDigits);
            emitChange(newDigits);
            focusInput(index + 1);
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const newDigits = [...digits];
            if (digits[index]) {
                newDigits[index] = '';
                setDigits(newDigits);
                emitChange(newDigits);
            } else if (index > 0) {
                newDigits[index - 1] = '';
                setDigits(newDigits);
                emitChange(newDigits);
                focusInput(index - 1);
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focusInput(index - 1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            focusInput(index + 1);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, length);
        if (!pasted) return;

        const newDigits = Array(length).fill('');
        pasted.split('').forEach((c, i) => { newDigits[i] = c; });
        setDigits(newDigits);
        emitChange(newDigits);
        focusInput(Math.min(pasted.length, length - 1));
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <Container>
            {digits.map((digit, i) => (
                <React.Fragment key={i}>
                    {i === 3 && <Separator>-</Separator>}
                    <DigitInput
                        ref={el => inputsRef.current[i] = el}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleChange(e, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        onPaste={handlePaste}
                        onFocus={handleFocus}
                        autoFocus={autoFocus && i === 0}
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        $hasValue={!!digit}
                    />
                </React.Fragment>
            ))}
        </Container>
    );
};

export default OTPInput;
